import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import type {
  EmployeeSkillEntry,
  DevelopmentGoalEntry,
  SkillDiffResult,
  SkillHistoryEntry,
  GoalSuggestion,
  EmployeePersonaGapResult,
  CertificationEntry,
} from '../types';
import {
  fetchEmployeeSkills,
  fetchEmployeeGoals,
  fetchGoalSuggestions,
  fetchSkillDiff,
  fetchSkillHistory,
  createGoal,
  fetchSkillNames,
} from '../api/skillDevelopment';
import { fetchEmployeePersonaGaps } from '../api/analytics';
import { fetchCertifications, addCertification } from '../api/training';
import SkillBadge from '../components/SkillBadge';

// In a real setup, this would come from the auth context.
// For now, we'll get the employee ID from localStorage or a config endpoint.
function useCurrentEmployeeId(): string | null {
  const [empId, setEmpId] = useState<string | null>(null);
  useEffect(() => {
    // Try to get from localStorage (set during login flow)
    const stored = localStorage.getItem('current_employee_id');
    if (stored) {
      setEmpId(stored);
      return;
    }
    // Fallback: fetch from /api/v1/employees/me
    import('../api/client').then(({ default: apiClient }) => {
      apiClient.get('/employees/me')
        .then((res) => {
          const id = res.data?.id;
          if (id) {
            localStorage.setItem('current_employee_id', id);
            setEmpId(id);
          }
        })
        .catch(() => {});
    });
  }, []);
  return empId;
}

type TabKey = 'skills' | 'personas' | 'certifications' | 'goals' | 'progress';

const TABS: { key: TabKey; labelKey: string }[] = [
  { key: 'skills', labelKey: 'mySkills.tabSkills' },
  { key: 'personas', labelKey: 'mySkills.tabPersonas' },
  { key: 'certifications', labelKey: 'mySkills.tabCertifications' },
  { key: 'goals', labelKey: 'mySkills.tabGoals' },
  { key: 'progress', labelKey: 'mySkills.tabProgress' },
];

export default function MySkills() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();
  const [activeTab, setActiveTab] = useState<TabKey>('skills');

  if (!employeeId) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">{t('mySkills.loading', 'Loading your profile...')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('mySkills.title', 'My Skills & Development')}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('mySkills.subtitle', 'View your skill profile, development goals, and track your progress.')}
        </p>
      </div>

      <nav aria-label={t('mySkills.tabNavigation', 'My skills sections')} className="border-b border-gray-200">
        <div className="flex gap-4 -mb-px" role="tablist">
          {TABS.map(({ key, labelKey }) => (
            <button
              key={key}
              role="tab"
              type="button"
              aria-selected={activeTab === key}
              onClick={() => setActiveTab(key)}
              className={[
                'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                activeTab === key
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
              ].join(' ')}
            >
              {t(labelKey, key)}
            </button>
          ))}
        </div>
      </nav>

      {activeTab === 'skills' && <MySkillsTab employeeId={employeeId} />}
      {activeTab === 'personas' && <MyPersonasTab employeeId={employeeId} />}
      {activeTab === 'certifications' && <MyCertificationsTab employeeId={employeeId} />}
      {activeTab === 'goals' && <MyGoalsTab employeeId={employeeId} />}
      {activeTab === 'progress' && <MyProgressTab employeeId={employeeId} />}
    </div>
  );
}

// ---- Skills Tab ----
function MySkillsTab({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const [skills, setSkills] = useState<EmployeeSkillEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchEmployeeSkills(employeeId)
      .then((data) => { if (!cancelled) setSkills(data); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (skills.length === 0) return <EmptyState message={t('mySkills.noSkills', 'No skills recorded yet. Your skills will appear here after your CV is processed.')} />;

  const grouped = skills.reduce<Record<string, EmployeeSkillEntry[]>>((acc, s) => {
    const cat = s.category ?? 'Other';
    (acc[cat] = acc[cat] ?? []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card">
          <p className="text-sm text-gray-500">{t('mySkills.totalSkills', 'Total Skills')}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{skills.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">{t('mySkills.avgLevel', 'Avg Level')}</p>
          <p className="mt-1 text-2xl font-bold text-brand-600">
            {(skills.reduce((s, sk) => s + sk.proficiency, 0) / skills.length).toFixed(1)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">{t('mySkills.categories', 'Categories')}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{Object.keys(grouped).length}</p>
        </div>
      </div>

      {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, catSkills]) => (
        <div key={category}>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">{category}</h3>
          <div className="space-y-2">
            {catSkills.sort((a, b) => b.proficiency - a.proficiency).map((skill) => (
              <div key={skill.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
                <span className="w-40 font-medium text-gray-900 truncate" title={skill.skill_name}>{skill.skill_name}</span>
                <div className="flex-1 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={[
                        'h-6 flex-1 rounded',
                        level <= skill.proficiency
                          ? level >= 4 ? 'bg-green-500' : level >= 3 ? 'bg-blue-500' : level >= 2 ? 'bg-amber-500' : 'bg-red-400'
                          : 'bg-gray-200',
                      ].join(' ')}
                    />
                  ))}
                </div>
                <span className="text-sm font-semibold text-gray-700 w-8 text-center">{skill.proficiency}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Personas Tab ----
function MyPersonasTab({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<EmployeePersonaGapResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchEmployeePersonaGaps(employeeId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!data || data.persona_gaps.length === 0) return <EmptyState message={t('mySkills.noPersonas', 'No matching personas found.')} />;

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        {t('mySkills.personasDesc', 'Personas that match your skill profile, ranked by fit score.')}
      </p>
      {data.persona_gaps.map((gap, index) => {
        const pct = Math.round(gap.fit_score * 100);
        const isTop = index === 0;
        return (
          <div
            key={gap.persona_id}
            className={[
              'rounded-xl border p-4',
              isTop ? 'border-brand-300 bg-brand-50' : 'border-gray-200 bg-white',
            ].join(' ')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{gap.persona_name}</h3>
                {isTop && (
                  <span className="inline-flex items-center rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                    {t('mySkills.bestMatch', 'Best Match')}
                  </span>
                )}
              </div>
              <span className={[
                'rounded-full px-3 py-1 text-sm font-semibold',
                pct >= 80 ? 'bg-green-100 text-green-700' :
                pct >= 60 ? 'bg-blue-100 text-blue-700' :
                pct >= 40 ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700',
              ].join(' ')}>
                {pct}%
              </span>
            </div>

            {/* Score bar */}
            <div className="mt-2 h-2 w-full rounded-full bg-gray-200" aria-hidden="true">
              <div
                className={[
                  'h-2 rounded-full transition-all',
                  pct >= 80 ? 'bg-green-500' :
                  pct >= 60 ? 'bg-blue-500' :
                  pct >= 40 ? 'bg-amber-500' :
                  'bg-red-500',
                ].join(' ')}
                style={{ width: `${pct}%` }}
              />
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {gap.matched_skills.map((s) => (
                <SkillBadge key={s} skill={s} matched />
              ))}
              {gap.missing_skills.map((s) => (
                <SkillBadge key={s} skill={s} matched={false} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Certifications Tab ----
function MyCertificationsTab({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const [certs, setCerts] = useState<CertificationEntry[]>([]);
  const [skillNames, setSkillNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ skillName: '', certName: '', issuingBody: '', earnedDate: '', expiryDate: '', credentialUrl: '' });
  const [saving, setSaving] = useState(false);

  const loadCerts = useCallback(() => {
    setLoading(true);
    fetchCertifications(employeeId)
      .then(setCerts)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [employeeId]);

  useEffect(() => { loadCerts(); }, [loadCerts]);

  useEffect(() => {
    fetchSkillNames().then(setSkillNames).catch(() => {});
  }, []);

  const skillOptions = useMemo(() =>
    skillNames.map((s) => ({ value: s, label: s })),
    [skillNames],
  );

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.skillName || !form.certName) return;
    setSaving(true);
    try {
      await addCertification(employeeId, {
        skillName: form.skillName, certName: form.certName,
        issuingBody: form.issuingBody || undefined, earnedDate: form.earnedDate || undefined,
        expiryDate: form.expiryDate || undefined, credentialUrl: form.credentialUrl || undefined,
      });
      setShowAdd(false);
      setForm({ skillName: '', certName: '', issuingBody: '', earnedDate: '', expiryDate: '', credentialUrl: '' });
      loadCerts();
    } finally { setSaving(false); }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">{t('mySkills.certsDesc', 'Your professional certifications and credentials.')}</p>
        <button type="button" onClick={() => setShowAdd(!showAdd)} className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700">
          {showAdd ? t('common.cancel', 'Cancel') : t('mySkills.addCert', '+ Add Certification')}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="rounded-lg border border-brand-200 bg-brand-50 p-4 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Select
              options={skillOptions}
              value={skillOptions.find((o) => o.value === form.skillName) ?? null}
              onChange={(opt) => setForm({ ...form, skillName: opt?.value ?? '' })}
              placeholder={t('mySkills.certSkill', 'Related Skill')}
              isClearable
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
                option: (base) => ({ ...base, fontSize: '0.875rem' }),
              }}
            />
            <input type="text" required placeholder={t('mySkills.certName', 'Certification Name')} value={form.certName} onChange={(e) => setForm({ ...form, certName: e.target.value })} className="rounded-md border-gray-300 text-sm" />
            <input type="text" placeholder={t('mySkills.certIssuer', 'Issuing Body')} value={form.issuingBody} onChange={(e) => setForm({ ...form, issuingBody: e.target.value })} className="rounded-md border-gray-300 text-sm" />
            <input type="date" placeholder={t('mySkills.certEarned', 'Earned Date')} value={form.earnedDate} onChange={(e) => setForm({ ...form, earnedDate: e.target.value })} className="rounded-md border-gray-300 text-sm" />
            <input type="date" placeholder={t('mySkills.certExpiry', 'Expiry Date')} value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} className="rounded-md border-gray-300 text-sm" />
            <input type="url" placeholder={t('mySkills.certUrl', 'Credential URL')} value={form.credentialUrl} onChange={(e) => setForm({ ...form, credentialUrl: e.target.value })} className="rounded-md border-gray-300 text-sm" />
          </div>
          <button type="submit" disabled={saving} className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            {saving ? t('common.saving', 'Saving...') : t('mySkills.saveCert', 'Save Certification')}
          </button>
        </form>
      )}

      {certs.length === 0 && !showAdd ? (
        <EmptyState message={t('mySkills.noCerts', 'No certifications recorded yet.')} />
      ) : (
        <div className="space-y-3">
          {certs.map((c) => (
            <div key={c.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-gray-900">{c.cert_name}</span>
                  {c.verified && <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">{t('mySkills.verified', 'Verified')}</span>}
                </div>
                <span className="text-xs text-gray-500 max-w-[150px] truncate" title={c.skill_name}>{c.skill_name}</span>
              </div>
              <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                {c.issuing_body && <span>{c.issuing_body}</span>}
                {c.earned_date && <span>{t('mySkills.earned', 'Earned')}: {c.earned_date}</span>}
                {c.expiry_date && <span className={new Date(c.expiry_date) < new Date() ? 'text-red-600 font-medium' : ''}>{t('mySkills.expires', 'Expires')}: {c.expiry_date}</span>}
                {c.credential_url && <a href={c.credential_url} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">{t('mySkills.viewCredential', 'View')}</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Goals Tab ----
function MyGoalsTab({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const [goals, setGoals] = useState<DevelopmentGoalEntry[]>([]);
  const [suggestions, setSuggestions] = useState<GoalSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([fetchEmployeeGoals(employeeId), fetchGoalSuggestions(employeeId)])
      .then(([g, s]) => { setGoals(g); setSuggestions(s); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [employeeId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAccept = async (s: GoalSuggestion) => {
    await createGoal(employeeId, {
      skillName: s.skill_name, currentLevel: s.current_level,
      targetLevel: s.suggested_target_level,
      targetDate: new Date(Date.now() + 180 * 86400000).toISOString().split('T')[0],
      notes: s.reason, source: 'PERSONA_GAP', personaId: s.persona_id,
    });
    loadData();
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {goals.length === 0 ? (
        <EmptyState message={t('mySkills.noGoals', 'No development goals set yet.')} />
      ) : (
        <div className="space-y-3">
          {goals.map((goal) => (
            <div key={goal.id} className={[
              'rounded-xl border p-4',
              goal.status === 'COMPLETED' ? 'border-green-200 bg-green-50' :
              goal.status === 'CANCELLED' ? 'border-gray-200 bg-gray-50 opacity-60' :
              'border-gray-200 bg-white',
            ].join(' ')}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-900 max-w-[200px] truncate" title={goal.skill_name}>{goal.skill_name}</span>
                <span className={[
                  'rounded-full px-2 py-0.5 text-xs font-medium',
                  goal.status === 'ACTIVE' ? 'bg-blue-100 text-blue-700' :
                  goal.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-600',
                ].join(' ')}>{goal.status}</span>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <span className="text-xs text-gray-500">
                  {t('mySkills.level', 'Level')} {goal.current_level} → {goal.target_level}
                </span>
                <div className="flex-1 h-2 rounded-full bg-gray-200">
                  <div className="h-2 rounded-full bg-brand-500" style={{ width: `${Math.min(100, (goal.current_level / goal.target_level) * 100)}%` }} />
                </div>
                {goal.target_date && <span className="text-xs text-gray-400">{t('mySkills.due', 'Due')}: {goal.target_date}</span>}
              </div>
              {goal.notes && <p className="mt-2 text-xs text-gray-500">{goal.notes}</p>}
            </div>
          ))}
        </div>
      )}

      {suggestions.length > 0 && (
        <div>
          <h3 className="text-md font-semibold text-gray-700 mb-2">{t('mySkills.suggestions', 'Suggested Goals')}</h3>
          <div className="space-y-2">
            {suggestions.map((s) => (
              <div key={`${s.skill_name}-${s.persona_id}`} className="flex items-center justify-between rounded-lg border border-dashed border-brand-300 bg-brand-50 p-3">
                <div>
                  <span className="font-medium text-gray-900 max-w-[200px] truncate block" title={s.skill_name}>{s.skill_name}</span>
                  <p className="text-xs text-gray-500 mt-0.5">{s.reason}</p>
                </div>
                <button type="button" onClick={() => handleAccept(s)} className="shrink-0 rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700">
                  {t('mySkills.accept', 'Accept')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Progress Tab ----
function MyProgressTab({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const [diff, setDiff] = useState<SkillDiffResult | null>(null);
  const [history, setHistory] = useState<SkillHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchSkillDiff(employeeId), fetchSkillHistory(employeeId)])
      .then(([d, h]) => { if (!cancelled) { setDiff(d); setHistory(h); } })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {diff && (diff.added.length > 0 || diff.removed.length > 0) ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="font-semibold text-gray-900 mb-3">{t('mySkills.latestChanges', 'Latest Skill Changes')}</h3>
          {diff.added.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-green-700 mb-1">{t('mySkills.newSkills', 'New Skills')} (+{diff.added.length})</p>
              <div className="flex flex-wrap gap-1.5">{diff.added.map((s) => <SkillBadge key={s} skill={s} matched />)}</div>
            </div>
          )}
          {diff.removed.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-700 mb-1">{t('mySkills.removedSkills', 'Removed')} (-{diff.removed.length})</p>
              <div className="flex flex-wrap gap-1.5">{diff.removed.map((s) => <SkillBadge key={s} skill={s} matched={false} />)}</div>
            </div>
          )}
        </div>
      ) : (
        <EmptyState message={t('mySkills.noDiff', 'No skill changes detected yet.')} />
      )}

      {history.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-3">{t('mySkills.history', 'Extraction History')}</h3>
          <div className="space-y-3">
            {history.map((entry, idx) => (
              <div key={entry.id} className={['rounded-lg border p-3', idx === 0 ? 'border-brand-200 bg-brand-50' : 'border-gray-200 bg-white'].join(' ')}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">{entry.skill_count} {t('mySkills.skills', 'skills')}</span>
                  <span className="text-xs text-gray-500">{new Date(entry.extracted_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Shared ----
function LoadingSpinner() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-12" role="status">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500" />
      <span className="sr-only">{t('common.loading', 'Loading...')}</span>
    </div>
  );
}
function ErrorMessage({ message }: { message: string }) {
  return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">Error: {message}</div>;
}
function EmptyState({ message }: { message: string }) {
  return <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">{message}</div>;
}
